import { ref, computed, watch } from 'vue';

export interface UserPreferences {
  theme: 'light' | 'dark' | 'auto';
  notifications: {
    email: boolean;
    browser: boolean;
    marketing: boolean;
  };
  privacy: {
    tracking: boolean;
    dataSharing: boolean;
  };
  accessibility: {
    reduceMotion: boolean;
    highContrast: boolean;
  };
  language: string;
  timezone: string;
}

export interface UserPreferencesOptions {
  storageKey?: string;
  autoSave?: boolean;
  defaultPreferences?: Partial<UserPreferences>;
}

/**
 * Reusable user preferences management composable
 * Provides common preference patterns that can be used by application stores
 */
export function useUserPreferences(options: UserPreferencesOptions = {}) {
  const { storageKey = 'userPreferences', autoSave = true, defaultPreferences = {} } = options;

  // Default preferences
  const defaultPrefs: UserPreferences = {
    theme: 'dark',
    notifications: {
      email: true,
      browser: true,
      marketing: false,
    },
    privacy: {
      tracking: false,
      dataSharing: false,
    },
    accessibility: {
      reduceMotion: false,
      highContrast: false,
    },
    language: 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    ...defaultPreferences,
  };

  // State
  const preferences = ref<UserPreferences>({ ...defaultPrefs });
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Computed getters
  const isDarkMode = computed(() => {
    if (preferences.value.theme === 'auto') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return preferences.value.theme === 'dark';
  });

  const hasNotificationsEnabled = computed(() => {
    return preferences.value.notifications.email || preferences.value.notifications.browser;
  });

  const isAccessibilityEnabled = computed(() => {
    return (
      preferences.value.accessibility.reduceMotion || preferences.value.accessibility.highContrast
    );
  });

  // Actions
  const updatePreferences = (newPreferences: Partial<UserPreferences>) => {
    preferences.value = { ...preferences.value, ...newPreferences };
    if (autoSave) {
      savePreferences();
    }
  };

  const updateTheme = (theme: UserPreferences['theme']) => {
    updatePreferences({ theme });
  };

  const updateNotifications = (notifications: Partial<UserPreferences['notifications']>) => {
    updatePreferences({
      notifications: { ...preferences.value.notifications, ...notifications },
    });
  };

  const updatePrivacy = (privacy: Partial<UserPreferences['privacy']>) => {
    updatePreferences({
      privacy: { ...preferences.value.privacy, ...privacy },
    });
  };

  const updateAccessibility = (accessibility: Partial<UserPreferences['accessibility']>) => {
    updatePreferences({
      accessibility: { ...preferences.value.accessibility, ...accessibility },
    });
  };

  const updateLanguage = (language: string) => {
    updatePreferences({ language });
  };

  const updateTimezone = (timezone: string) => {
    updatePreferences({ timezone });
  };

  const savePreferences = () => {
    try {
      isLoading.value = true;
      error.value = null;
      localStorage.setItem(storageKey, JSON.stringify(preferences.value));
    } catch (err) {
      error.value = 'Failed to save preferences';
      console.error('Failed to save user preferences:', err);
    } finally {
      isLoading.value = false;
    }
  };

  const loadPreferences = () => {
    try {
      isLoading.value = true;
      error.value = null;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        preferences.value = { ...defaultPrefs, ...parsed };
      }
    } catch (err) {
      error.value = 'Failed to load preferences';
      console.error('Failed to load user preferences:', err);
      preferences.value = { ...defaultPrefs };
    } finally {
      isLoading.value = false;
    }
  };

  const resetPreferences = () => {
    preferences.value = { ...defaultPrefs };
    if (autoSave) {
      savePreferences();
    }
  };

  const clearError = () => {
    error.value = null;
  };

  // Apply theme to document
  const applyTheme = () => {
    const root = document.documentElement;
    if (isDarkMode.value) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  };

  // Apply accessibility settings
  const applyAccessibility = () => {
    const root = document.documentElement;

    if (preferences.value.accessibility.reduceMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }

    if (preferences.value.accessibility.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }
  };

  // Watch for theme changes and apply them
  watch(
    () => preferences.value.theme,
    () => {
      applyTheme();
    },
    { immediate: true }
  );

  // Watch for accessibility changes and apply them
  watch(
    () => preferences.value.accessibility,
    () => {
      applyAccessibility();
    },
    { deep: true, immediate: true }
  );

  // Watch for system theme changes when in auto mode
  if (typeof window !== 'undefined') {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', () => {
      if (preferences.value.theme === 'auto') {
        applyTheme();
      }
    });
  }

  // Initialize preferences on creation
  loadPreferences();

  return {
    // State
    preferences: computed(() => preferences.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),

    // Computed getters
    isDarkMode,
    hasNotificationsEnabled,
    isAccessibilityEnabled,

    // Actions
    updatePreferences,
    updateTheme,
    updateNotifications,
    updatePrivacy,
    updateAccessibility,
    updateLanguage,
    updateTimezone,
    savePreferences,
    loadPreferences,
    resetPreferences,
    clearError,
    applyTheme,
    applyAccessibility,
  };
}
